import {Button, Dropdown, DropdownItem} from 'platform/components';
import {Box, BoxProps, Right} from 'platform/foundation';
import styled from 'styled-components';

import {useMemo} from 'react';

import {
  InspectionType,
  useCreateAuditMutation,
  useGetInspectionTemplatesQuery,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {testIds, vehiclesRoutes} from '@dms/routes';
import {usePermissions} from '@dms/shared';

import {composePath, useNavigate, useRequiredParams} from 'shared/web';

export const ScrollAreaWrapper = styled.div`
  overflow-y: auto;
  max-height: 18.75rem;
`;

interface NewInspectionButtonProps {
  vehicleId?: string;
  openLinkInNewTab?: boolean;
  getRedirectRoute?: (id: string, inspectionId: string) => string;
  onAfterSubmit?: (inspectionId: string) => void | Promise<void>;
  wrapperBoxProps?: BoxProps;
}

const EXCLUDED_INSPECTION_TYPES = [InspectionType.VEHICLE_CONDITION];

const INSPECTION_TYPES_FOR_CREATE_INSPECTION = Object.values(InspectionType).filter(
  (type) => !EXCLUDED_INSPECTION_TYPES.includes(type)
);

export const NewInspectionButton = (props: NewInspectionButtonProps) => {
  const {id: vehicleId} = useRequiredParams();
  const navigate = useNavigate();
  // const inspectionTemplates = useSelector(selectInspectionsStructureTemplates);
  const [createAuditV2, {isLoading: isCreateAuditLoading}] = useCreateAuditMutation();
  const {data: inspectionTemplates} = useGetInspectionTemplatesQuery({isActive: true});

  const filteredInspectionTemplates = useMemo(
    () =>
      inspectionTemplates?.filter(
        (template) => !EXCLUDED_INSPECTION_TYPES.includes(template.inspectionType)
      ),
    [inspectionTemplates]
  );
  const [hasCreateInspectionPermission] = usePermissions({
    permissionKeys: ['createInspection'],
    scopes: {
      createInspection: {
        inspectionType: {
          match: 'some',
          values: INSPECTION_TYPES_FOR_CREATE_INSPECTION,
        },
      },
    },
  });

  const handleCreateInspection = async (
    auditStructureId: string,
    inspectionType: InspectionType
  ) => {
    const saneVehicleId = props.vehicleId ? props.vehicleId : vehicleId;

    const response = await createAuditV2({
      vehicleId: saneVehicleId,
      body: {
        inspectionType,
        auditStructureId,
      },
    }).unwrap();

    await props.onAfterSubmit?.(response.id);

    const newLink =
      props.getRedirectRoute?.(saneVehicleId, response.id) ??
      composePath(vehiclesRoutes.inspectionDetail, {
        params: {
          id: saneVehicleId,
          inspectionId: response.id,
        },
      });

    if (props.openLinkInNewTab) {
      return window.open(newLink, '_blank');
    }

    navigate(newLink);
  };

  if (!filteredInspectionTemplates || !hasCreateInspectionPermission) {
    return null;
  }

  return (
    <Box {...props.wrapperBoxProps}>
      <Right>
        <Dropdown
          data-testid={testIds.vehicles.conditions('newInspection')}
          placement="bottom-end"
          dropdownControl={
            <Button
              data-testid={testIds.vehicles.conditions('newInspection-dropdownControl')}
              title={i18n.t('entity.condition.labels.newInspection')}
              isLoading={isCreateAuditLoading}
            />
          }
        >
          <ScrollAreaWrapper data-testid={testIds.vehicles.conditions('scrollAreaWrapper')}>
            {filteredInspectionTemplates.map((template, index) => (
              <DropdownItem
                key={`$template-${template.id}`}
                data-testid={testIds.vehicles.conditions(`newInspection-[${index}]`)}
                label={template.name}
                onClick={() => handleCreateInspection(template.id, template.inspectionType)}
              />
            ))}
          </ScrollAreaWrapper>
        </Dropdown>
      </Right>
    </Box>
  );
};
